/**
 * Floating help chat (Chat de dúvidas): round button + conversation panel.
 * State and API calls live in useHelpChat; rendering of AI text in HelpMessageText.
 */

import React, { useEffect, useRef } from 'react';
import {
  ArrowLeft,
  History,
  ImagePlus,
  Loader2,
  MessageCircleQuestion,
  Plus,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import type { HelpMessage } from '@/services/helpService';
import { useHelpEnabled } from './helpChatEvents';
import { HelpMessageText, HelpTeamMessage } from './HelpMessageText';
import { useHelpChat } from './useHelpChat';

const UNREAD_CHECK_INTERVAL_MS = 60_000;
const EXAMPLE_KEYS = ['help.example1', 'help.example2', 'help.example3', 'help.example4'];

export default function HelpChat() {
  const enabled = useHelpEnabled();
  if (enabled !== true) return null;
  return <HelpChatWidget />;
}

function UserBubble({ message }: { message: HelpMessage }) {
  const { t } = useTranslation();
  return (
    <div className="flex max-w-[85%] flex-col items-end gap-1.5 self-end">
      {message.image_url && (
        <a href={message.image_url} target="_blank" rel="noopener noreferrer" aria-label={t('help.openImage')}>
          <img
            src={message.image_url}
            alt=""
            className="max-h-40 rounded-xl border border-border object-cover"
          />
        </a>
      )}
      {message.text && (
        <div className="whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-sm leading-relaxed text-primary-foreground">
          {message.text}
        </div>
      )}
    </div>
  );
}

function AssistantBubble({
  message,
  onRate,
}: {
  message: HelpMessage;
  onRate: (id: string, value: 1 | -1) => void;
}) {
  const { t } = useTranslation();
  const rated = message.feedback !== null;
  return (
    <div className="flex max-w-[92%] flex-col gap-1 self-start">
      <div className="rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
        <HelpMessageText text={message.text} />
      </div>
      <div className="flex items-center gap-1 pl-1">
        <button
          type="button"
          disabled={rated}
          onClick={() => onRate(message.id, 1)}
          aria-label={t('help.feedbackUp')}
          title={t('help.feedbackUp')}
          className={cn(
            'rounded-md p-1 text-muted-foreground transition-colors enabled:hover:bg-accent',
            message.feedback === 1 && 'text-primary',
            rated && message.feedback !== 1 && 'opacity-40'
          )}
        >
          <ThumbsUp className="size-3.5" />
        </button>
        <button
          type="button"
          disabled={rated}
          onClick={() => onRate(message.id, -1)}
          aria-label={t('help.feedbackDown')}
          title={t('help.feedbackDown')}
          className={cn(
            'rounded-md p-1 text-muted-foreground transition-colors enabled:hover:bg-accent',
            message.feedback === -1 && 'text-primary',
            rated && message.feedback !== -1 && 'opacity-40'
          )}
        >
          <ThumbsDown className="size-3.5" />
        </button>
        {message.feedback === -1 && (
          <span className="text-xs text-muted-foreground">{t('help.feedbackThanks')}</span>
        )}
        {message.knew === false && message.feedback !== -1 && (
          <span className="text-xs text-muted-foreground">{t('help.notKnown')}</span>
        )}
      </div>
    </div>
  );
}

function HelpChatWidget() {
  const { t } = useTranslation();
  const chat = useHelpChat();
  const {
    open,
    setOpen,
    view,
    messages,
    conversations,
    conversationId,
    text,
    setText,
    image,
    busy,
    loading,
    error,
    unread,
    refreshUnread,
    openUnread,
  } = chat;
  const location = useLocation();
  const lastCheckRef = useRef(Date.now());
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(open);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Team-reply check: on mount, on window focus, and on navigation (at most once per minute).
  useEffect(() => {
    void refreshUnread();
    const onFocus = () => {
      lastCheckRef.current = Date.now();
      void refreshUnread();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshUnread]);

  useEffect(() => {
    if (Date.now() - lastCheckRef.current < UNREAD_CHECK_INTERVAL_MS) return;
    lastCheckRef.current = Date.now();
    void refreshUnread();
  }, [location.pathname, refreshUnread]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, view, open]);

  useEffect(() => {
    if (open && view === 'conversation') textareaRef.current?.focus();
  }, [open, view, conversationId]);

  // Hide the Crisp bubble while the panel is open (it would cover the send button); restore only if it was visible.
  useEffect(() => {
    if (!open) return;
    const crisp = (window as any).$crisp;
    if (!crisp || typeof crisp.push !== 'function') return;
    const wasVisible = typeof crisp.is === 'function' ? crisp.is('chat:visible') : true;
    crisp.push(['do', 'chat:hide']);
    return () => {
      if (wasVisible) crisp.push(['do', 'chat:show']);
    };
  }, [open]);

  // Escape closes the panel; focus returns to the launcher.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !open) launcherRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void chat.send(text);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const file = Array.from<File>(e.clipboardData.files).find((f: File) => f.type.startsWith('image/'));
    if (file) {
      e.preventDefault();
      chat.attach(file);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm(`${t('help.deleteTitle')}\n${t('help.deleteDescription')}`)) {
      void chat.remove(id);
    }
  };

  if (!open) {
    const replied = unread !== null;
    return (
      <button
        ref={launcherRef}
        type="button"
        onClick={replied ? openUnread : () => setOpen(true)}
        title={replied ? t('help.teamReplied') : t('help.buttonTitle')}
        aria-label={replied ? t('help.teamReplied') : t('help.buttonTitle')}
        className={cn(
          'fixed bottom-[calc(88px+env(safe-area-inset-bottom))] right-5 z-50 flex h-11 items-center justify-center bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105',
          replied ? 'gap-2 rounded-full px-4 text-sm font-medium' : 'size-11 rounded-full'
        )}
      >
        {replied ? (
          <>
            <span className="size-2 animate-pulse rounded-full bg-primary-foreground" aria-hidden />
            {t('help.teamReplied')}
          </>
        ) : (
          <MessageCircleQuestion className="size-5" />
        )}
      </button>
    );
  }

  const canSend = !busy && (text.trim().length > 0 || image !== null);

  return (
    <section
      role="dialog"
      aria-label={t('help.title')}
      className={cn(
        'fixed z-50 flex flex-col overflow-hidden border-border bg-card text-foreground shadow-2xl',
        'inset-0 h-full w-full rounded-none',
        'sm:inset-auto sm:bottom-3 sm:right-3 sm:h-[min(600px,calc(100dvh-1.5rem))] sm:w-[min(400px,calc(100vw-1.5rem))] sm:rounded-2xl sm:border'
      )}
    >
      <header className="flex items-center gap-1 border-b border-border px-3 py-2.5">
        {view === 'history' ? (
          <button
            type="button"
            onClick={() => chat.setView('conversation')}
            aria-label={t('help.back')}
            title={t('help.back')}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"
          >
            <ArrowLeft className="size-4" />
          </button>
        ) : null}
        <h2 className="flex-1 truncate px-1 text-sm font-semibold">
          {view === 'history' ? t('help.conversations') : t('help.title')}
        </h2>
        {view === 'conversation' && (
          <button
            type="button"
            onClick={chat.showHistory}
            aria-label={t('help.conversations')}
            title={t('help.conversations')}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"
          >
            <History className="size-4" />
          </button>
        )}
        <button
          type="button"
          onClick={chat.newConversation}
          aria-label={t('help.newConversation')}
          title={t('help.newConversation')}
          className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"
        >
          <Plus className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t('help.close')}
          title={t('help.close')}
          className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"
        >
          <X className="size-4" />
        </button>
      </header>

      {view === 'history' ? (
        <div ref={scrollRef} className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
          {conversations === null ? (
            <Loader2 className="m-auto size-5 animate-spin text-muted-foreground" aria-label={t('help.thinking')} />
          ) : conversations.length === 0 ? (
            <p className="m-auto px-4 text-center text-sm text-muted-foreground">{t('help.noConversations')}</p>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                className="group flex items-center gap-1 rounded-xl hover:bg-accent"
              >
                <button
                  type="button"
                  onClick={() => chat.resume(c.id)}
                  className="flex min-w-0 flex-1 flex-col items-start gap-0.5 px-3 py-2 text-left"
                >
                  <span className="flex w-full items-center gap-2">
                    <span className="truncate text-sm font-medium">{c.title}</span>
                    {c.unread > 0 && (
                      <span className="shrink-0 rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                        {c.unread}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(c.updated_at).toLocaleString()}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(c.id)}
                  aria-label={t('help.delete')}
                  title={t('help.delete')}
                  className="mr-1 rounded-lg p-2 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))
          )}
          {error && <p role="alert" className="px-3 py-2 text-xs text-destructive">{error}</p>}
        </div>
      ) : (
        <>
          <div
            ref={scrollRef}
            role="log"
            aria-live="polite"
            className="flex flex-1 flex-col gap-3 overflow-y-auto p-3"
          >
            {loading ? (
              <div className="m-auto flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {t('help.thinking')}
              </div>
            ) : messages.length === 0 && !busy ? (
              <div className="m-auto flex w-full flex-col gap-2">
                <p className="mb-1 text-center text-sm text-muted-foreground">{t('help.emptyIntro')}</p>
                {EXAMPLE_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => void chat.send(t(key))}
                    className="rounded-xl border border-border px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            ) : (
              messages.map((m) => (
                <React.Fragment key={m.id}>
                  {m.role === 'user' ? (
                    <UserBubble message={m} />
                  ) : m.role === 'team' ? (
                    <HelpTeamMessage text={m.text} />
                  ) : (
                    <AssistantBubble message={m} onRate={chat.rate} />
                  )}
                </React.Fragment>
              ))
            )}
            {busy && (
              <div className="flex items-center gap-2 self-start rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5 text-sm text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                {t('help.thinking')}
              </div>
            )}
          </div>

          <div className="border-t border-border p-2.5">
            {error && (
              <p role="alert" className="mb-2 px-1 text-xs text-destructive">
                {error}
              </p>
            )}
            {image && (
              <div className="relative mb-2 inline-block">
                <img src={image.url} alt="" className="h-16 rounded-lg border border-border object-cover" />
                <button
                  type="button"
                  onClick={chat.clearImage}
                  aria-label={t('help.removeImage')}
                  title={t('help.removeImage')}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-foreground p-0.5 text-background"
                >
                  <X className="size-3" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  chat.attach(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                aria-label={t('help.attach')}
                title={t('help.attach')}
                className="rounded-lg p-2 text-muted-foreground hover:bg-accent"
              >
                <ImagePlus className="size-5" />
              </button>
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                rows={1}
                maxLength={2000}
                aria-label={t('help.placeholder')}
                placeholder={t('help.placeholder')}
                className="max-h-28 min-h-9 flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2 text-base outline-none sm:text-sm focus-visible:ring-1 focus-visible:ring-ring"
              />
              <button
                type="button"
                onClick={() => void chat.send(text)}
                disabled={!canSend}
                aria-label={t('help.send')}
                title={t('help.send')}
                className="rounded-lg bg-primary p-2 text-primary-foreground transition-opacity disabled:opacity-40"
              >
                <Send className="size-5" />
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
